import "./Ratings.scss";
import { useState, useEffect } from "react";
import DeleteUser from "./../../assets/delete.svg";
import { DataGrid } from "@mui/x-data-grid";
import { RatingColumns } from "../../SourceData";
import api from "../../utils/api";
import SwitchButton from "../../components/SwitchButton/SwitchButton";
import { Tooltip } from "@mui/material";
import { toast } from "react-toastify";
import MessageModals from "../../components/MessageModals/MessageModals";
import { usePermissions } from "./../../hooks/usePermissions";

const Ratings = () => {
  const { hasPermission } = usePermissions();

  const [openDelete, setOpenDelete] = useState(false);
  const [deletedItem, setDeletedItem] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Ratings data state
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch ratings data with proper pagination
  const fetchRatings = async () => {
    // Check view permission before fetching data
    if (!hasPermission(87)) {
      toast.error("ليس لديك صلاحية لعرض التقييمات");
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(`/ratings?page=${page}&limit=${pageSize}`);

      if (response.data.status === "success") {
        setRatings(response.data.data);
        setTotalCount(response.data.totalCount);
        setTotalPages(response.data.totalPages);
      }
    } catch (error) {
      console.error("Error fetching ratings:", error);
      toast.error("خطأ في جلب البيانات");
    } finally {
      setLoading(false);
    }
  };

  // Effect to fetch ratings when dependencies change
  useEffect(() => {
    fetchRatings();
  }, [page, pageSize]);

  // Toggle rating active state
  const handleActive = async (id) => {
    // Check activate permission
    if (!hasPermission(88)) {
      toast.error("ليس لديك صلاحية لتفعيل/إلغاء تفعيل التقييمات");
      return;
    }

    setLoading(true);
    try {
      await api.patch(`/ratings/${id}/toggle`);
      toast.success("تم تغيير الحالة بنجاح");
      fetchRatings();
    } catch (error) {
      toast.error("خطأ في تغيير الحالة");
    } finally {
      setLoading(false);
    }
  };

  // Handle delete rating
  const handleDelete = (rating) => {
    // Check delete permission
    if (!hasPermission(89)) {
      toast.error("ليس لديك صلاحية لحذف التقييمات");
      return;
    }

    setDeletedItem(rating);
    setOpenDelete(true);
  };

  const actionColumn = [
    {
      field: "isActive",
      headerName: "الحالة",
      minWidth: 60,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const isActive = params?.row?.isActive === true;
        const canActivate = hasPermission(88);

        return (
          <Tooltip
            title={
              canActivate
                ? isActive
                  ? "إلغاء التفعيل"
                  : "تفعيل"
                : "ليس لديك صلاحية لتغيير الحالة"
            }
          >
            <div
              style={{
                opacity: canActivate ? 1 : 0.5,
                cursor: canActivate ? "pointer" : "not-allowed",
              }}
            >
              <SwitchButton
                checked={isActive}
                onClick={() => handleActive(params.row._id)}
                disabled={!canActivate}
              />
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "action",
      headerName: "",
      width: 80,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const canDelete = hasPermission(89);

        return (
          <div className="cell-action">
            <Tooltip
              title={canDelete ? "حذف التقييم" : "ليس لديك صلاحية لحذف التقييم"}
            >
              <div
                className="cell-btn"
                onClick={() => handleDelete(params.row)}
                style={{
                  cursor: canDelete ? "pointer" : "not-allowed",
                  opacity: canDelete ? 1 : 0.5,
                }}
              >
                <img src={DeleteUser} alt="حذف" />
              </div>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  // If user doesn't have view permission, show restricted access message
  if (!hasPermission(87)) {
    return (
      <div className="products">
        <div className="special-header">
          <p className="title">اراء العملاء</p>
        </div>
        <div
          className="container"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
            backgroundColor: "white",
            borderRadius: "8px",
          }}
        >
          <p
            style={{
              fontSize: "18px",
              color: "#666",
              textAlign: "center",
            }}
          >
            ليس لديك صلاحية لعرض التقييمات
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="products">
      <div className="special-header">
        <p className="title">
          اراء العملاء <span className="count-badge">{totalCount || 0}</span>
        </p>
      </div>
      <div className="container">
        <DataGrid
          sx={{ backgroundColor: "white", width: "100%" }}
          autoHeight={true}
          columns={RatingColumns.concat(actionColumn)}
          rows={ratings.map((rating, index) => ({
            ...rating,
            // Generate unique id if not present
            id: rating._id || index,
            // Add index for numbering
            index: (page - 1) * pageSize + index + 1,
          }))}
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          loading={loading}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          initialState={{
            pagination: {
              paginationModel: { pageSize, page: page - 1 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          onPaginationModelChange={(model) => {
            // Handle page change
            if (model.page !== page - 1) {
              setPage(model.page + 1);
            }

            // Handle page size change
            if (model.pageSize !== pageSize) {
              setPageSize(model.pageSize);
              setPage(1); // Reset to first page when changing page size
            }
          }}
          getRowClassName={(params) => "data-grid-row"}
          localeText={{
            MuiTablePagination: {
              labelRowsPerPage: "تقييم في الصفحة",
            },
          }}
        />
      </div>

      {/* Only render delete modal if user has delete permission */}
      {hasPermission(89) && (
        <MessageModals
          open={openDelete}
          currentView="delete"
          deleteMessage="تم حذف التقييم بنجاح"
          route={`/ratings/${deletedItem?._id}`}
          onDataChange={fetchRatings}
          onClose={() => {
            setOpenDelete(false);
            setDeletedItem(null);
          }}
        />
      )}
    </div>
  );
};

export default Ratings;
